import React, { useContext, useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Paper,
  Stack,
  Tab,
  Tabs,
  Grid,
  IconButton,
  Chip,
  LinearProgress,
} from "@mui/material";
import AppTooltip from "../../components/AppTooltip";
import HubPageHeader from "../../components/HubPageHeader";
import {
  Download as SaveAltIcon,
  Users as GroupsIcon,
  Phone as CallIcon,
  Hourglass as HourglassIcon,
  CheckCircle2 as CheckCircleIcon,
  Headphones as VoiceOverIcon,
  UserPlus as GroupAddIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  BarChart3 as InsertChartIcon,
  Users as PeopleAltIcon,
} from "lucide-react";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { isArray } from "lodash";
import { AuthContext } from "../../context/Auth/AuthContext";
import useDashboard from "../../hooks/useDashboard";
import TableAttendantsStatus from "../../components/Dashboard/TableAttendantsStatus";
import { ChatsUser } from "./ChartsUser";
import ChartDonut from "./ChartDonut";
import { ChartsDate } from "./ChartsDate";
import ForbiddenPage from "../../components/ForbiddenPage";
import { i18n } from "../../translate/i18n";

import { useTheme as useThemeV4 } from "@material-ui/core/styles";
import {
  useTheme as useThemeV5,
  createTheme as createThemeV5,
  ThemeProvider as ThemeProviderV5,
} from "@mui/material/styles";

const tLang = (key, fallback) => {
  const v = i18n.t(key);
  return !v || v === key ? fallback : v;
};

const hexToRgba = (hex, alpha = 1) => {
  if (!hex || typeof hex !== "string") return `rgba(25,118,210,${alpha})`;
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return `rgba(25,118,210,${alpha})`;
  return `rgba(${r},${g},${b},${alpha})`;
};

const buildTokens = (isDark) => ({
  pageBg: isDark ? "#121212" : "#ffffff",
  cardBg: isDark ? "#11141b" : "#ffffff",
  cardBgSoft: isDark ? "#161a22" : "#fafbfd",
  border: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
  borderStrong: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(15,23,42,0.09)",
  shadow: isDark
    ? "0 1px 0 rgba(255,255,255,0.02) inset, 0 8px 24px rgba(0,0,0,0.45)"
    : "0 1px 0 rgba(255,255,255,0.6) inset, 0 8px 24px rgba(15,23,42,0.06)",
  shadowHover: isDark
    ? "0 1px 0 rgba(255,255,255,0.02) inset, 0 14px 36px rgba(0,0,0,0.55)"
    : "0 1px 0 rgba(255,255,255,0.7) inset, 0 14px 36px rgba(15,23,42,0.10)",
  textPrimary: isDark ? "#e9edef" : "#0f172a",
  textSecondary: isDark ? "#8696a0" : "#64748b",
  textMuted: isDark ? "#5b6770" : "#94a3b8",
  divider: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.08)",
  tabBarBg: isDark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
  tabBarBorder: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(15,23,42,0.06)",
  tabSelectedBg: isDark ? "#1c2230" : "#ffffff",
  tabSelectedShadow: isDark
    ? "0 1px 0 rgba(255,255,255,0.04) inset, 0 4px 14px rgba(0,0,0,0.45)"
    : "0 1px 0 rgba(255,255,255,0.9) inset, 0 4px 14px rgba(15,23,42,0.08)",
});

const StatCard = ({ title, value, icon, color, tokens }) => (
  <Card
    sx={{
      position: "relative",
      height: "100%",
      borderRadius: 3,
      background: tokens.cardBg,
      border: tokens.border,
      boxShadow: tokens.shadow,
      transition: "transform .25s ease, box-shadow .25s ease, border-color .25s ease",
      overflow: "hidden",
      "&:hover": {
        transform: "translateY(-3px)",
        boxShadow: tokens.shadowHover,
        borderColor: hexToRgba(color, 0.45),
      },
      "&:before": {
        content: '""',
        position: "absolute",
        inset: 0,
        background: `linear-gradient(135deg, ${hexToRgba(color, 0.10)} 0%, transparent 55%)`,
        pointerEvents: "none",
      },
    }}
  >
    <CardContent sx={{ p: 2.25, position: "relative" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 0.6,
              textTransform: "uppercase",
              color: tokens.textSecondary,
              mb: 0.5,
            }}
            noWrap
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontSize: 28,
              fontWeight: 800,
              lineHeight: 1.1,
              color: tokens.textPrimary,
              letterSpacing: -0.4,
            }}
          >
            {value}
          </Typography>
        </Box>
        <Avatar
          sx={{
            width: 44,
            height: 44,
            background: `linear-gradient(135deg, ${color} 0%, ${hexToRgba(color, 0.7)} 100%)`,
            color: "#fff",
            boxShadow: `0 6px 16px ${hexToRgba(color, 0.35)}`,
          }}
        >
          {icon}
        </Avatar>
      </Stack>
    </CardContent>
  </Card>
);

const NpsMetricCard = ({ title, value, color, tokens }) => (
  <Grid item xs={12} sm={4}>
    <Card
      sx={{
        height: "100%",
        textAlign: "left",
        p: 2.25,
        borderRadius: 3,
        background: tokens.cardBgSoft,
        border: tokens.border,
        boxShadow: "none",
        transition: "border-color .2s ease",
        "&:hover": { borderColor: hexToRgba(color, 0.4) },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: tokens.textSecondary }}>
          {title}
        </Typography>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: color, boxShadow: `0 0 0 4px ${hexToRgba(color, 0.18)}` }} />
      </Stack>
      <Typography sx={{ fontSize: 32, fontWeight: 800, color: tokens.textPrimary, letterSpacing: -0.6, lineHeight: 1.1 }}>
        {value}
        <Typography component="span" sx={{ fontSize: 16, fontWeight: 700, color: tokens.textSecondary, ml: 0.5 }}>%</Typography>
      </Typography>
      <LinearProgress
        variant="determinate"
        value={Math.max(0, Math.min(100, Number(value) || 0))}
        sx={{
          mt: 1.5,
          height: 6,
          borderRadius: 6,
          backgroundColor: hexToRgba(color, 0.12),
          "& .MuiLinearProgress-bar": {
            borderRadius: 6,
            background: `linear-gradient(90deg, ${color} 0%, ${hexToRgba(color, 0.75)} 100%)`,
          },
        }}
      />
    </Card>
  </Grid>
);

const SectionHeader = ({ icon, title, action, tokens, accent }) => (
  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Avatar
        sx={{
          width: 36,
          height: 36,
          background: `linear-gradient(135deg, ${accent} 0%, ${hexToRgba(accent, 0.7)} 100%)`,
          color: "#fff",
          boxShadow: `0 4px 12px ${hexToRgba(accent, 0.3)}`,
        }}
      >
        {icon}
      </Avatar>
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: tokens.textPrimary, letterSpacing: -0.2 }}>
        {title}
      </Typography>
    </Stack>
    {action}
  </Stack>
);

const Dashboard = () => {
  const themeV5 = useThemeV5();
  const themeV4 = useThemeV4();

  const isDark = (themeV4?.palette?.type || themeV4?.mode) === "dark";
  const PRIMARY_MAIN = themeV4?.palette?.primary?.main || "#1976d2";
  const PRIMARY_DARK = themeV4?.palette?.primary?.dark || "#115293";
  const PRIMARY_CONTRAST = themeV4?.palette?.primary?.contrastText || "#fff";

  const tokens = useMemo(() => buildTokens(isDark), [isDark]);

  const v5Theme = useMemo(
    () =>
      createThemeV5({
        palette: {
          mode: isDark ? "dark" : "light",
          primary: { main: PRIMARY_MAIN, dark: PRIMARY_DARK, contrastText: PRIMARY_CONTRAST },
          background: {
            default: isDark ? "#121212" : "#ffffff",
            paper: isDark ? "#0b0d12" : "#ffffff",
          },
          divider: isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)",
        },
        components: {
          MuiOutlinedInput: {
            styleOverrides: {
              root: { borderRadius: 12 },
            },
          },
          MuiFilledInput: {
            styleOverrides: {
              root: { borderRadius: 12 },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: { borderRadius: 12 },
            },
          },
          MuiToggleButton: {
            styleOverrides: {
              root: { borderRadius: 12 },
            },
          },
        },
      }),
    [isDark, PRIMARY_MAIN, PRIMARY_DARK, PRIMARY_CONTRAST]
  );

  const [counters, setCounters] = useState({});
  const [attendants, setAttendants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const { find } = useDashboard();
  const { user } = useContext(AuthContext);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = {
          date_from: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10),
          date_to: new Date().toISOString().slice(0, 10),
        };
        const data = await find(params);
        setCounters(data.counters);
        if (isArray(data.attendants)) setAttendants(data.attendants);
      } catch (error) {
        toast.error(i18n.t("dashboard.errors.loadData"));
        console.error(error);
      }
      setLoading(false);
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportToExcel = () => {
    try {
      const table = document.getElementById("grid-attendants");
      const ws = XLSX.utils.table_to_sheet(table);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, i18n.t("dashboard.export.sheetName"));
      XLSX.writeFile(wb, i18n.t("dashboard.export.fileName"));
    } catch {
      toast.error(i18n.t("dashboard.errors.exportExcel"));
    }
  };

  const getOnlineUsersCount = () => attendants.filter((u) => u.online).length;

  if (user.profile === "user" && user.showDashboard === "disabled") {
    return <ForbiddenPage />;
  }

  const statCards = [
    { title: i18n.t("dashboard.cards.inAttendance"), value: counters.supportHappening || 0, icon: <CallIcon size={20} />, color: PRIMARY_MAIN },
    { title: i18n.t("dashboard.cards.waiting"), value: counters.supportPending || 0, icon: <HourglassIcon size={20} />, color: themeV5.palette.info.main },
    { title: i18n.t("dashboard.cards.finalized"), value: counters.supportFinished || 0, icon: <CheckCircleIcon size={20} />, color: themeV5.palette.success.main },
    { title: i18n.t("dashboard.cards.groups"), value: counters.supportGroups || 0, icon: <GroupsIcon size={20} />, color: themeV5.palette.secondary.main },
    { title: i18n.t("dashboard.cards.activeAttendants"), value: `${getOnlineUsersCount()}/${attendants.length}`, icon: <VoiceOverIcon size={20} />, color: themeV5.palette.error.main },
    { title: i18n.t("dashboard.cards.newContacts"), value: counters.leads || 0, icon: <GroupAddIcon size={20} />, color: themeV5.palette.warning.main },
  ];

  const npsData = {
    score: counters.npsScore || 0,
    promoters: counters.npsPromotersPerc || 0,
    passives: counters.npsPassivePerc || 0,
    detractors: counters.npsDetractorsPerc || 0,
    totalTickets: counters.tickets || 0,
    withRating: counters.withRating || 0,
    percRating: counters.percRating || 0,
  };

  const npsColors = {
    [i18n.t("dashboard.assessments.prosecutors")]: "#2EA85A",
    [i18n.t("dashboard.assessments.detractors")]: "#F73A2C",
    [i18n.t("dashboard.assessments.neutral")]: "#F7C82C",
  };
  const npsChartData = [
    { name: i18n.t("dashboard.assessments.prosecutors"), value: npsData.promoters },
    { name: i18n.t("dashboard.assessments.detractors"), value: npsData.detractors },
    { name: i18n.t("dashboard.assessments.neutral"), value: npsData.passives },
  ].sort((a, b) => a.name.localeCompare(b.name));
  const sortedNpsColors = npsChartData.map((item) => npsColors[item.name]);

  const tabIcons = [<TrendingUpIcon size={16} />, <StarIcon size={16} />, <PeopleAltIcon size={16} />];

  const today = new Date();
  const periodLabel = today.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  return (
    <ThemeProviderV5 theme={v5Theme}>
      <Box
        sx={{
          background: tokens.pageBg,
          height: "100%",
          width: "100%",
          paddingTop: "12px",
          paddingBottom: "12px",
          paddingLeft: "12px",
          paddingRight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            paddingRight: "12px",
            ...(themeV4.scrollbarStyles || {}),
          }}
        >
        <HubPageHeader
          icon={<InsertChartIcon size={20} strokeWidth={1.8} />}
          title={tLang("dashboard.title", "Dashboard")}
          subtitle={tLang("dashboard.subtitle", "Visão geral do seu atendimento em tempo real")}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                icon={<InsertChartIcon size={14} color={PRIMARY_MAIN} />}
                label={periodLabel}
                sx={{
                  background: hexToRgba(PRIMARY_MAIN, isDark ? 0.18 : 0.10),
                  color: tokens.textPrimary,
                  border: `1px solid ${hexToRgba(PRIMARY_MAIN, 0.25)}`,
                  fontWeight: 600,
                  fontSize: 12,
                  textTransform: "capitalize",
                  "& .MuiChip-icon": { ml: 0.75 },
                }}
              />
              <Chip
                size="small"
                label={`${getOnlineUsersCount()} ${tLang("dashboard.status.online", "Online").toLowerCase()}`}
                sx={{
                  background: hexToRgba("#2EA85A", isDark ? 0.18 : 0.12),
                  color: isDark ? "#7be0a3" : "#1e7a3f",
                  border: `1px solid ${hexToRgba("#2EA85A", 0.3)}`,
                  fontWeight: 700,
                  fontSize: 12,
                  "&:before": {
                    content: '""',
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: "#2EA85A",
                    display: "inline-block",
                    marginLeft: "10px",
                    marginRight: "-2px",
                    boxShadow: "0 0 0 4px rgba(46,168,90,0.25)",
                    animation: "pulseDot 1.6s ease-in-out infinite",
                  },
                  "@keyframes pulseDot": {
                    "0%, 100%": { boxShadow: "0 0 0 0 rgba(46,168,90,0.45)" },
                    "50%": { boxShadow: "0 0 0 6px rgba(46,168,90,0)" },
                  },
                }}
              />
            </Stack>
          }
        />

        <Grid container spacing={2.25} sx={{ mb: { xs: 3, md: 4 } }}>
          {statCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
              <StatCard {...card} tokens={tokens} />
            </Grid>
          ))}
        </Grid>

        <Box
          sx={{
            display: "inline-flex",
            p: 0.5,
            mb: 2.5,
            borderRadius: 999,
            background: tokens.tabBarBg,
            border: tokens.tabBarBorder,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, nv) => setActiveTab(nv)}
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{
              minHeight: 36,
              "& .MuiTabs-flexContainer": { gap: 0.5 },
              "& .MuiTab-root": {
                minHeight: 36,
                px: 2,
                py: 0.75,
                borderRadius: 999,
                fontWeight: 600,
                fontSize: 13,
                textTransform: "none",
                color: tokens.textSecondary,
                transition: "all .2s ease",
                "&:hover": { color: tokens.textPrimary },
              },
              "& .Mui-selected": {
                color: `${tokens.textPrimary} !important`,
                background: tokens.tabSelectedBg,
                boxShadow: tokens.tabSelectedShadow,
              },
            }}
          >
            <Tab iconPosition="start" icon={tabIcons[0]} label={i18n.t("dashboard.tabs.performance")} />
            <Tab iconPosition="start" icon={tabIcons[1]} label="NPS" />
            <Tab iconPosition="start" icon={tabIcons[2]} label={i18n.t("dashboard.tabs.attendants")} />
          </Tabs>
        </Box>

        <Box>
          {activeTab === 0 && (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                pb: "12px",
                borderRadius: 3,
                background: tokens.cardBg,
                border: tokens.border,
                boxShadow: tokens.shadow,
              }}
            >
              <SectionHeader
                icon={<TrendingUpIcon size={18} />}
                title={i18n.t("dashboard.tabs.performance")}
                tokens={tokens}
                accent={PRIMARY_MAIN}
              />
              <ChartsDate />
            </Paper>
          )}

          {activeTab === 1 && (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 3,
                background: tokens.cardBg,
                border: tokens.border,
                boxShadow: tokens.shadow,
              }}
            >
              <SectionHeader
                icon={<StarIcon size={18} />}
                title={i18n.t("dashboard.tabs.assessments")}
                tokens={tokens}
                accent="#F7C82C"
              />
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      p: 2.25,
                      borderRadius: 3,
                      background: tokens.cardBgSoft,
                      border: tokens.border,
                      boxShadow: "none",
                    }}
                  >
                    <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: tokens.textSecondary, mb: 1 }}>
                      {i18n.t("dashboard.assessments.generalScore")}
                    </Typography>
                    <ChartDonut data={npsChartData} value={npsData.score} colors={sortedNpsColors} />
                  </Card>
                </Grid>

                <Grid item xs={12} md={8}>
                  <Grid container spacing={2}>
                    <NpsMetricCard
                      title={i18n.t("dashboard.assessments.prosecutors")}
                      value={npsData.promoters}
                      color={npsColors[i18n.t("dashboard.assessments.prosecutors")]}
                      tokens={tokens}
                    />
                    <NpsMetricCard
                      title={i18n.t("dashboard.assessments.neutral")}
                      value={npsData.passives}
                      color={npsColors[i18n.t("dashboard.assessments.neutral")]}
                      tokens={tokens}
                    />
                    <NpsMetricCard
                      title={i18n.t("dashboard.assessments.detractors")}
                      value={npsData.detractors}
                      color={npsColors[i18n.t("dashboard.assessments.detractors")]}
                      tokens={tokens}
                    />
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      background: tokens.cardBgSoft,
                      border: tokens.border,
                      boxShadow: "none",
                    }}
                  >
                    <Grid container textAlign="center">
                      {[
                        { title: i18n.t("dashboard.assessments.totalCalls"), value: npsData.totalTickets },
                        { title: i18n.t("dashboard.assessments.ratedCalls"), value: npsData.withRating },
                        { title: i18n.t("dashboard.assessments.evaluationIndex"), value: `${(npsData.percRating || 0).toFixed(1)}%` },
                      ].map((item, index) => (
                        <Grid
                          item
                          xs={12}
                          sm={4}
                          key={item.title}
                          sx={{
                            p: 2.25,
                            position: "relative",
                            borderRight: { sm: index < 2 ? `1px solid ${tokens.divider}` : "none" },
                            borderBottom: { xs: index < 2 ? `1px solid ${tokens.divider}` : "none", sm: "none" },
                          }}
                        >
                          <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: tokens.textSecondary, mb: 0.75 }}>
                            {item.title}
                          </Typography>
                          <Typography sx={{ fontSize: 24, fontWeight: 800, color: tokens.textPrimary, letterSpacing: -0.4 }}>
                            {item.value}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          )}

          {activeTab === 2 && (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 3,
                background: tokens.cardBg,
                border: tokens.border,
                boxShadow: tokens.shadow,
              }}
            >
              <SectionHeader
                icon={<PeopleAltIcon size={18} />}
                title={i18n.t("dashboard.tabs.attendants")}
                tokens={tokens}
                accent={themeV5.palette.error.main}
                action={
                  <AppTooltip title={tLang("dashboard.buttons.export", "Exportar")}>
                    <IconButton
                      onClick={exportToExcel}
                      size="small"
                      sx={{
                        background: `linear-gradient(135deg, ${PRIMARY_MAIN} 0%, ${PRIMARY_DARK} 100%)`,
                        color: PRIMARY_CONTRAST,
                        boxShadow: `0 6px 16px ${hexToRgba(PRIMARY_MAIN, 0.35)}`,
                        transition: "all .2s ease",
                        "&:hover": {
                          background: `linear-gradient(135deg, ${PRIMARY_DARK} 0%, ${PRIMARY_MAIN} 100%)`,
                          transform: "translateY(-1px)",
                          boxShadow: `0 10px 22px ${hexToRgba(PRIMARY_MAIN, 0.45)}`,
                        },
                      }}
                    >
                      <SaveAltIcon size={18} />
                    </IconButton>
                  </AppTooltip>
                }
              />
              <div id="grid-attendants">
                {attendants.length > 0 && <TableAttendantsStatus attendants={attendants} loading={loading} />}
              </div>
              <Box sx={{ mt: 4 }}>
                <SectionHeader
                  icon={<TrendingUpIcon size={18} />}
                  title={i18n.t("dashboard.charts.userPerformance")}
                  tokens={tokens}
                  accent={PRIMARY_MAIN}
                />
                <ChatsUser />
              </Box>
            </Paper>
          )}
        </Box>
        </Box>
      </Box>
    </ThemeProviderV5>
  );
};

export default Dashboard;
